<template>
  <div>
    <div v-if="!isLoading" style="display: flex; justify-content: flex-end">
      <v-tooltip bottom color="primary">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            class="mx-2"
            fab
            dark
            large
            color="primary"
            @click="fetchExportData"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon dark> mdi-download </v-icon>
          </v-btn>
        </template>
        <span>{{ $t('general.btn.download_consumption_devices_hover') }}</span>
      </v-tooltip>
    </div>
    <div v-else>
      <v-tooltip bottom color="primary">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            class="mx-2"
            fab
            dark
            large
            color="primary"
            @click="fetchExportData"
            v-bind="attrs"
            v-on="on"
          >
            <v-progress-circular
              indeterminate
              color="white"
            ></v-progress-circular>
          </v-btn>
        </template>
        <span>{{ $t('general.btn.download_consumption_devices_hover') }}</span>
      </v-tooltip>
    </div>

    <div v-if="isLoading && totalCount > 0" class="text-caption mt-1">
      {{
        $t('general.consumption_devices_export_progress', {
          loaded: loadedCount,
          total: totalCount,
        })
      }}
    </div>

    <div v-if="isError" class="mt-4">
      <NewAlert
        color="ERROR"
        :message="$t('error_message.consumption_devices_export')"
      />
    </div>
  </div>
</template>

<script>
import { NewAlert } from '@newnetzportal/netzportal-webapp-storybook-components';
import { exportConsumptionDevicesOrders } from '../../services/ConsumptionDevicesService';

const EXPORT_PAGE_RETRIES = 2;
const EXPORT_RETRY_DELAY_MS = 1000;
const XLSX_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const EXPORT_HEADERS = [
  'Auftrag ID',
  'Auftragsnummer',
  'Status',
  'Anfragedatum',
  'Letzte Aktualisierung',
  'Zuletzt geaendert von',
  'Antragsteller',
  'Antragsteller Rolle',
  'Antragsteller E-Mail',
  'Antragsteller Telefon',
  'Anschlussadresse',
  'Koordinaten (lat/lng)',
  'Geraetetyp',
  'Geraete-ID',
  'Leistung (kW)',
  'Hersteller',
  'Typ',
  'Art der Steuerung',
  'Lastenmanagement',
  'Anschluss zur Steuerbox',
  'Pruefbereit',
  'Zaehlernummer',
  'Abrechnungsmodul',
  'Inbetriebnahmedatum',
  'Installationsunternehmen',
  'Installationsunternehmen ID',
  'Netzanschlussnummer',
  'Bauart',
  'Art der Ladung',
  'Anzahl der Ladepunkte',
  'Aufstellungsort',
  'Wallbox-Koordinaten (lat/lng)',
  'Anzahl der Speichereinheiten',
  'Speicherbeladung',
  'Speicherentladung',
  'Speicherkapazitaet (kWh)',
];

export default {
  name: 'ConsumptionDevicesDownloadData',
  components: {
    NewAlert,
  },
  props: {
    dateRangeFilters: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      isError: false,
      loadedCount: 0,
      totalCount: 0,
    };
  },
  created() {
    // Kept non-reactive on purpose.
    this.abortController = null;
    this.exportWorker = null;
  },
  beforeDestroy() {
    this.abortController?.abort();
    this.exportWorker?.terminate();
  },
  methods: {
    async fetchExportData() {
      if (this.isLoading) {
        return;
      }

      const abortController = new AbortController();
      this.abortController = abortController;
      this.isLoading = true;
      this.isError = false;
      this.loadedCount = 0;
      this.totalCount = 0;

      try {
        const orders = await this.loadAllExportOrders(abortController.signal);
        const rows = this.buildExportRows(orders);
        const buffer = await this.buildExcelFile(rows);

        if (!abortController.signal.aborted) {
          this.downloadFile(
            buffer,
            `SteuVE_Data_${new Date().toISOString()}.xlsx`
          );
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          this.isError = true;
          setTimeout(() => {
            this.isError = false;
          }, 5000);
        }
      } finally {
        this.abortController = null;
        this.isLoading = false;
      }
    },
    async loadAllExportOrders(signal) {
      const queryBody = this.getQueryBody();
      const ordersById = new Map();
      let cursor = null;

      do {
        const page = await this.loadExportPage(queryBody, cursor, signal);
        if (!Array.isArray(page?.items)) {
          throw new Error('Export data is missing.');
        }
        if (page.next_cursor && page.next_cursor === cursor) {
          throw new Error('Export cursor did not advance.');
        }

        page.items.forEach((order) => ordersById.set(order.id, order));
        this.loadedCount = ordersById.size;
        this.totalCount = Math.max(page.count || 0, ordersById.size);
        cursor = page.next_cursor;
      } while (cursor);

      return Array.from(ordersById.values());
    },
    async loadExportPage(queryBody, cursor, signal) {
      for (let attempt = 0; ; attempt++) {
        try {
          return await exportConsumptionDevicesOrders(queryBody, {
            cursor,
            signal,
          });
        } catch (error) {
          if (
            signal.aborted ||
            error?.response?.status === 401 ||
            attempt >= EXPORT_PAGE_RETRIES
          ) {
            throw error;
          }

          await new Promise((resolve) =>
            setTimeout(resolve, EXPORT_RETRY_DELAY_MS * (attempt + 1))
          );
        }
      }
    },
    buildExcelFile(rows) {
      return new Promise((resolve, reject) => {
        const worker = new Worker(
          new URL('./consumptionDevicesExport.worker.js', import.meta.url)
        );
        this.exportWorker = worker;

        const finish = () => {
          worker.terminate();
          this.exportWorker = null;
        };

        worker.onmessage = ({ data }) => {
          finish();
          if (data?.error) {
            reject(new Error(data.error));
            return;
          }
          resolve(data.buffer);
        };
        worker.onerror = (event) => {
          finish();
          reject(new Error(event.message));
        };

        worker.postMessage({
          rows,
          headers: EXPORT_HEADERS,
          sheetName: 'SteuVE',
        });
      });
    },
    downloadFile(buffer, fileName) {
      const url = URL.createObjectURL(
        new Blob([buffer], { type: XLSX_MIME_TYPE })
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
    getQueryBody() {
      return {
        status:
          this.$route.query.status === 'all' || !this.$route.query?.status
            ? 'all'
            : this.$route.query.status,
        search_word: this.$route.query.search ? this.$route.query.search : '',
        start_date: this.dateRangeFilters.start || null,
        end_date: this.dateRangeFilters.end || null,
      };
    },
    buildExportRows(orders) {
      if (!Array.isArray(orders)) {
        return [];
      }

      const rows = [];

      orders.forEach((order) => {
        const orderRow = this.buildOrderRow(order);
        const deviceRows = this.buildDeviceRows(order);

        if (deviceRows.length === 0) {
          rows.push(orderRow);
          return;
        }

        deviceRows.forEach((deviceRow) => {
          rows.push({
            ...orderRow,
            ...deviceRow,
          });
        });
      });

      return rows;
    },
    buildOrderRow(order) {
      const applicantRole = this.toText(this.getValue(order, 'applicant_role'));

      return {
        'Auftrag ID': this.toText(this.getValue(order, 'id')),
        Auftragsnummer: this.toText(this.getValue(order, 'case_number')),
        Status: this.translateStatus(this.getValue(order, 'status')),
        Anfragedatum: this.toText(this.getValue(order, 'request_date')),
        'Letzte Aktualisierung': this.toText(
          this.getValue(order, 'last_update')
        ),
        'Zuletzt geaendert von': this.toText(
          this.getValue(order, 'last_changed_by')
        ),
        Antragsteller: this.formatApplicant(order, applicantRole),
        'Antragsteller Rolle': this.translateApplicantRole(applicantRole),
        'Antragsteller E-Mail': this.resolveApplicantContact(
          order,
          applicantRole,
          'email'
        ),
        'Antragsteller Telefon': this.resolveApplicantContact(
          order,
          applicantRole,
          'phone_number'
        ),
        Anschlussadresse: this.formatAddress(order),
        'Koordinaten (lat/lng)': this.formatCoordinates(order, 'coordinates'),
        Geraetetyp: '',
        'Geraete-ID': '',
        'Leistung (kW)': '',
        Hersteller: '',
        Typ: '',
        'Art der Steuerung': '',
        Lastenmanagement: '',
        'Anschluss zur Steuerbox': '',
        Pruefbereit: '',
        Zaehlernummer: '',
        Abrechnungsmodul: '',
        Inbetriebnahmedatum: '',
        Installationsunternehmen: '',
        'Installationsunternehmen ID': '',
        Netzanschlussnummer: '',
        Bauart: '',
        'Art der Ladung': '',
        'Anzahl der Ladepunkte': '',
        Aufstellungsort: '',
        'Wallbox-Koordinaten (lat/lng)': '',
        'Anzahl der Speichereinheiten': '',
        Speicherbeladung: '',
        Speicherentladung: '',
        'Speicherkapazitaet (kWh)': '',
      };
    },
    buildDeviceRows(order) {
      const rows = [];
      const controllableDevices = order?.controllable_devices;

      if (!controllableDevices || typeof controllableDevices !== 'object') {
        return rows;
      }

      Object.entries(controllableDevices).forEach(
        ([deviceType, devicesById]) => {
          if (!devicesById || typeof devicesById !== 'object') {
            return;
          }

          Object.entries(devicesById).forEach(([deviceId, rawDevice]) => {
            rows.push(
              this.buildDeviceRow(deviceType, deviceId, rawDevice || {})
            );
          });
        }
      );

      return rows;
    },
    buildDeviceRow(deviceType, deviceId, device) {
      return {
        Geraetetyp: this.translateDeviceType(deviceType),
        'Geraete-ID': this.toText(deviceId),
        'Leistung (kW)': this.toKilowatt(
          this.getValue(device, 'performance_W')
        ),
        Hersteller: this.toText(this.getValue(device, 'manufacturer')),
        Typ: this.toText(this.getValue(device, 'type')),
        'Art der Steuerung': this.translateControlType(
          this.getValue(device, 'control_type')
        ),
        Lastenmanagement: this.translateLoadManagement(
          this.getValue(device, 'load_management')
        ),
        'Anschluss zur Steuerbox': this.translateControlBoxConnection(
          this.getValue(device, 'control_box_connection')
        ),
        Pruefbereit: this.translateBoolean(
          this.getValue(device, 'ready_for_inspection')
        ),
        Zaehlernummer: this.toText(this.getValue(device, 'counter_id')),
        Abrechnungsmodul: this.translateBillingModule(
          this.getValue(device, 'billing_module')
        ),
        Inbetriebnahmedatum: this.toText(
          this.getValue(device, 'commissioning_date')
        ),
        Installationsunternehmen: this.toText(
          this.getValue(device, 'installation_company_electricity')
        ),
        'Installationsunternehmen ID': this.toText(
          this.getValue(device, 'installation_company_electricity_id')
        ),
        Netzanschlussnummer: this.firstNonEmpty([
          this.getValue(device, 'supply_installation_number'),
          this.getValue(device, 'supply_installation_numberF'),
        ]),
        Bauart: this.translateInstallationType(
          this.getValue(device, 'installation_type')
        ),
        'Art der Ladung': this.translateChargingType(
          this.getValue(device, 'charging_type')
        ),
        'Anzahl der Ladepunkte': this.toText(
          this.getValue(device, 'charging_points')
        ),
        Aufstellungsort: this.toText(
          this.getValue(device, 'installation_location')
        ),
        'Wallbox-Koordinaten (lat/lng)': this.formatCoordinates(
          device,
          'wallbox_coordinates'
        ),
        'Anzahl der Speichereinheiten': this.toText(
          this.getValue(device, 'number_of_storage_units')
        ),
        Speicherbeladung: this.translateStorageValue(
          this.getValue(device, 'storage_charging')
        ),
        Speicherentladung: this.translateStorageValue(
          this.getValue(device, 'storage_discharging')
        ),
        'Speicherkapazitaet (kWh)': this.toKilowatt(
          this.getValue(device, 'storage_capacity_Wh')
        ),
      };
    },
    formatApplicant(order, applicantRole) {
      const applicantPrefixes =
        applicantRole === 'end_customer'
          ? ['end_customer', 'applicant']
          : ['applicant', 'end_customer'];

      for (const prefix of applicantPrefixes) {
        const title = this.toText(this.getValue(order, `${prefix}.title`));
        const companyName = this.toText(
          this.getValue(order, `${prefix}.company_name`)
        );
        const cityName = this.toText(
          this.getValue(order, `${prefix}.city_name`)
        );
        const fullName = [
          this.toText(this.getValue(order, `${prefix}.name`)),
          this.toText(this.getValue(order, `${prefix}.surname`)),
        ]
          .filter(Boolean)
          .join(' ');

        if (title === 'Firma' && companyName) {
          return companyName;
        }

        if (title === 'Stadt/Kommune' && cityName) {
          return cityName;
        }

        const value = this.firstNonEmpty([fullName, companyName, cityName]);
        if (value) {
          return value;
        }
      }

      return '';
    },
    resolveApplicantContact(order, applicantRole, fieldName) {
      const applicantPrefixes =
        applicantRole === 'end_customer'
          ? ['end_customer', 'applicant']
          : ['applicant', 'end_customer'];

      return this.firstNonEmpty(
        applicantPrefixes.map((prefix) =>
          this.getValue(order, `${prefix}.${fieldName}`)
        )
      );
    },
    formatAddress(order) {
      if (!order || typeof order !== 'object') {
        return '';
      }

      const streetLine = [
        this.toText(this.getValue(order, 'address.street')),
        this.toText(this.getValue(order, 'address.street_number')),
      ]
        .filter(Boolean)
        .join(' ');
      const cityLine = [
        this.toText(this.getValue(order, 'address.zip')),
        this.toText(this.getValue(order, 'address.city')),
      ]
        .filter(Boolean)
        .join(' ')
        .trim();
      const country = this.toText(this.getValue(order, 'address.country'));

      return [streetLine, cityLine, country].filter(Boolean).join(', ');
    },
    formatCoordinates(source, fieldPrefix = '') {
      const lat = fieldPrefix
        ? this.toText(this.getValue(source, `${fieldPrefix}.lat`))
        : this.toText(source?.lat);
      const lng = fieldPrefix
        ? this.toText(this.getValue(source, `${fieldPrefix}.lng`))
        : this.toText(source?.lng);

      if (!lat && !lng) {
        return '';
      }

      return `${lat}, ${lng}`;
    },
    translateStatus(status) {
      const normalizedStatus = this.toText(status);
      if (!normalizedStatus) {
        return '';
      }

      return this.translateByKey(
        `consumption_devices.${normalizedStatus}`,
        normalizedStatus
      );
    },
    translateApplicantRole(applicantRole) {
      const normalizedRole = this.toText(applicantRole);
      if (!normalizedRole) {
        return '';
      }

      return this.firstNonEmpty([
        this.translateByKey(
          `consumption_devices.order_details.${normalizedRole}`,
          ''
        ),
        this.translateByKey(
          `consumption_devices.order_details.${normalizedRole}_role`,
          ''
        ),
        this.translateByKey(`contract_contact.${normalizedRole}`, ''),
        normalizedRole,
      ]);
    },
    translateDeviceType(deviceType) {
      const normalizedDeviceType = this.toText(deviceType);
      if (!normalizedDeviceType) {
        return '';
      }

      return this.translateByKey(
        `consumption_devices.${normalizedDeviceType}`,
        normalizedDeviceType
      );
    },
    translateBillingModule(billingModule) {
      const normalizedBillingModule = this.toText(billingModule);

      switch (normalizedBillingModule) {
        case 'reduced_flat':
          return 'Modul 1 (Pauschale Netzentgeltreduzierung)';
        case 'reduced_separate':
          return 'Modul 2 (Prozentuale Arbeitspreisreduzierung)';
        default:
          return normalizedBillingModule;
      }
    },
    translateControlType(controlType) {
      const normalizedControlType = this.toText(controlType);
      if (!normalizedControlType) {
        return '';
      }

      return this.translateByKey(
        `consumption_devices.order_details.${normalizedControlType}`,
        normalizedControlType
      );
    },
    translateLoadManagement(loadManagement) {
      const normalizedLoadManagement = this.toText(loadManagement);
      if (!normalizedLoadManagement) {
        return '';
      }

      const lowerCaseLoadManagement = normalizedLoadManagement.toLowerCase();
      if (
        lowerCaseLoadManagement === 'lastmanagement nicht definiert' ||
        lowerCaseLoadManagement === 'lastenmanagement nicht definiert'
      ) {
        return '';
      }

      if (normalizedLoadManagement === 'not_available') {
        return this.translateByKey(
          'consumption_devices.order_details.load_management_not_available',
          normalizedLoadManagement
        );
      }

      if (normalizedLoadManagement === 'available') {
        return this.translateByKey(
          'consumption_devices.order_details.load_management_available',
          normalizedLoadManagement
        );
      }

      return normalizedLoadManagement;
    },
    translateControlBoxConnection(controlBoxConnection) {
      const normalizedControlBoxConnection = this.toText(controlBoxConnection);
      if (!normalizedControlBoxConnection) {
        return '';
      }

      if (
        normalizedControlBoxConnection === 'relay' ||
        normalizedControlBoxConnection === 'control_relay_box'
      ) {
        return this.translateByKey(
          'consumption_devices.order_details.relay',
          normalizedControlBoxConnection
        );
      }

      return normalizedControlBoxConnection;
    },
    translateInstallationType(installationType) {
      const normalizedInstallationType = this.toText(installationType);
      if (!normalizedInstallationType) {
        return '';
      }

      if (normalizedInstallationType === 'charging_station') {
        return 'Ladesaeule';
      }

      if (normalizedInstallationType === 'wallbox') {
        return this.translateByKey(
          'consumption_devices.wallbox',
          normalizedInstallationType
        );
      }

      return normalizedInstallationType;
    },
    translateChargingType(chargingType) {
      const normalizedChargingType = this.toText(chargingType);
      if (!normalizedChargingType) {
        return '';
      }

      if (normalizedChargingType === 'AC_charger') {
        return 'AC Lader';
      }

      if (normalizedChargingType === 'DC_charger') {
        return 'DC Lader';
      }

      return normalizedChargingType;
    },
    translateStorageValue(storageValue) {
      const normalizedStorageValue = this.toText(storageValue);
      if (!normalizedStorageValue) {
        return '';
      }

      return this.translateByKey(
        `consumption_devices.order_details.${normalizedStorageValue}`,
        normalizedStorageValue
      );
    },
    translateBoolean(value) {
      if (value === true || value === 'true') {
        return this.translateByKey(
          'consumption_devices.order_details.yes',
          'Ja'
        );
      }

      if (value === false || value === 'false') {
        return this.translateByKey(
          'consumption_devices.order_details.no',
          'Nein'
        );
      }

      return this.toText(value);
    },
    toKilowatt(valueInWattOrWh) {
      if (
        valueInWattOrWh === undefined ||
        valueInWattOrWh === null ||
        valueInWattOrWh === ''
      ) {
        return '';
      }

      const numericValue = Number(valueInWattOrWh);
      if (Number.isNaN(numericValue)) {
        return this.toText(valueInWattOrWh);
      }

      const valueInKilo = numericValue / 1000;
      const rounded = Math.round(valueInKilo * 1000) / 1000;

      return this.toText(rounded);
    },
    translateByKey(key, fallbackValue = '') {
      const translated = this.$t(key);
      if (typeof translated !== 'string' || translated === key) {
        return this.toText(fallbackValue);
      }

      return translated;
    },
    firstNonEmpty(values) {
      if (!Array.isArray(values)) {
        return '';
      }

      for (const value of values) {
        const textValue = this.toText(value);
        if (textValue) {
          return textValue;
        }
      }

      return '';
    },
    getValue(source, path) {
      if (!source || typeof source !== 'object' || !path) {
        return undefined;
      }

      if (Object.prototype.hasOwnProperty.call(source, path)) {
        return source[path];
      }

      const segments = path.split('.');
      let currentValue = source;

      for (const segment of segments) {
        if (
          !currentValue ||
          typeof currentValue !== 'object' ||
          !Object.prototype.hasOwnProperty.call(currentValue, segment)
        ) {
          return undefined;
        }

        currentValue = currentValue[segment];
      }

      return currentValue;
    },
    toText(value) {
      if (value === undefined || value === null) {
        return '';
      }

      return String(value);
    },
  },
};
</script>
